import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_EMAIL, FIELD_QUANTITY } from '@components/dynamic-input-fields/constants';
import { Field } from '@components/dynamic-input-fields/dynamic-input-fields.type';
import { EventSummaryCard } from '@components/event-summary-card';
import { IconPerson } from '@components/icons/icon-person';
import { Loader } from '@components/loader';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import {
  InviteGuestForm,
  MULTIPLE_INVITE,
  SINGLE_INVITE,
} from '@components/process-invite-guest/process-invite-guest.type';
import { SendButtonText } from '@components/send-button-text/send-button-text';
import { XS_VIEWPORT } from '@constants/viewport';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useGetInvitation } from '@hooks/invitation/use-get-invitation';
import { useWindowSize } from '@hooks/use-window-size';
import { filterRequiredFormFields } from '@utils/collected-form-fields/filter-required-form-fields';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { INFO_TYPE_FIELD, InviteGuestFillInfoProps } from './invite-guest.type';
import { MultiInviteGuest } from './multi-invite';
import { InviteGuestSingleInvite } from './single-invite';

export const FillInfo = ({ onContinue, handleClose, isSubmitting }: InviteGuestFillInfoProps) => {
  const isMobile = useWindowSize(XS_VIEWPORT);
  const { t, i18n } = useTranslation();
  const {
    getValues,
    watch,
    formState: { isValid },
  } = useFormContext<InviteGuestForm>();
  const invitationId: string = getValues('invitationId');
  const eventId = getValues('eventId');
  const quantity = getValues(FIELD_QUANTITY);
  const inviteType = getValues('type');
  const watchedSelectedOption = watch(INFO_TYPE_FIELD);
  const watchedEmail = watch(FIELD_EMAIL);

  const { role } = useContext(CompanyAuthContext);
  const { data: invitation } = useGetInvitation({ invitationId });
  const { data: event } = useGetEvent({ eventId });

  // TODO: https://xceeddev.atlassian.net/browse/XCEED-6332 Create send link invite guest process.
  // const options: FillInfoOption[] = useMemo(() => {
  //   const toggleOption = (option: typeof OPTION_FILL_INFO | typeof OPTION_SEND_LINK) => {
  //     if (watchedSelectedOption === option) return [];
  //     setValue(INFO_TYPE_FIELD, option, {
  //       shouldDirty: true,
  //       shouldValidate: true,
  //       shouldTouch: true,
  //     });
  //   };
  //   return [
  //     {
  //       name: t('invitations:fillInfoOption'),
  //       bordered: true,
  //       subtitle: t('invitations:fillInfoSubtitle'),
  //       id: OPTION_FILL_INFO as typeof OPTION_FILL_INFO,
  //       icon: IconPencil,
  //       active: watchedSelectedOption === OPTION_FILL_INFO,
  //       wrapperClasses: 'flex-1',
  //       action: () => toggleOption(OPTION_FILL_INFO),
  //     },
  //     {
  //       name: t('invitations:sendLinkOption'),
  //       bordered: true,
  //       subtitle: t('invitations:sendLinkSubtitle'),
  //       id: OPTION_SEND_LINK as typeof OPTION_SEND_LINK,
  //       icon: IconSend,
  //       active: watchedSelectedOption === OPTION_SEND_LINK,
  //       wrapperClasses: 'flex-1',
  //       action: () => toggleOption(OPTION_SEND_LINK),
  //     },
  //   ];
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [t, watchedSelectedOption]);

  if (!event || !invitation)
    return (
      <div className='relative max-h-[80vh] overflow-y-auto'>
        <Loader />
      </div>
    );

  const invitationName = getTranslatedPropertyBasedOnOrder({
    translations: invitation?.name,
    preferredLanguage: i18n.language,
  });

  const requiredFields = filterRequiredFormFields(invitation.bookingFormFields) as Field[];

  return (
    <div className='relative max-h-[80vh] overflow-y-auto'>
      <ModalHeader title={t('invitations:fillInfoTitle')} handleClose={handleClose} />
      <div className='flex flex-col px-4 sm:px-20 py-6 text-left'>
        <div className='flex flex-col gap-2 mb-4'>
          <h2 className='text-headline-m line-clamp-2 text-primary'>{invitationName}</h2>
          {quantity > 0 && (
            <p className='flex text-body-s gap-2 text-secondary'>
              <IconPerson className='w-4 h-4' /> {t('invitations:guests', { count: quantity })}
            </p>
          )}
        </div>
        <EventSummaryCard
          locale={i18n.language}
          name={event.name}
          startingTime={event.startingTime}
          coverUrl={event.coverUrl}
          clubName={event.club.name}
          timezone={event.club.city.timezone}
        />
      </div>
      {inviteType === SINGLE_INVITE && (
        <InviteGuestSingleInvite
          options={[]}
          requiredFields={requiredFields}
          isoCode={event.club.city.country.isoCode}
        />
      )}
      {inviteType === MULTIPLE_INVITE && (
        <MultiInviteGuest
          title={t('invitations:inviteListInstructionsLabel')}
          instructionsTitle={t('invitations:inviteListInstructionsTitle')}
          instructionsDescription={t('invitations:inviteListInstructionsText')}
          textareaPlaceholder={t('invitations:inviteListTextareaPlaceholder')}
          offer={invitation}
          roleName={role?.name}
        />
      )}
      <ModalFooter
        primaryButtonText={
          <span className='flex items-center gap-2 whitespace-nowrap'>
            <SendButtonText
              watchedSelectedOption={watchedSelectedOption}
              email={watchedEmail}
              t={t}
              compact={isMobile}
            />
          </span>
        }
        onPrimaryClick={onContinue}
        onSecondaryClick={handleClose}
        secondaryButtonText={t('common:discard')}
        isPrimaryDisabled={!isValid}
        isPrimaryLoading={isSubmitting}
      />
    </div>
  );
};
